import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Plus, Trash2, Copy, ChevronUp, ChevronDown, Database, Info,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────

export interface GridSortSpec {
  field: string;
  direction: 'asc' | 'desc';
}

export interface GridColumnDef {
  key: string;
  label: string;
  group: string;
}

interface GridSortDialogProps {
  open: boolean;
  onClose: () => void;
  sortSpecs: GridSortSpec[];
  onApply: (specs: GridSortSpec[]) => void;
  columns: GridColumnDef[];
  maxLevels?: number;
}

// ─── Virtual fields that can't be sorted server-side ────────────────
const UNSORTABLE_FIELDS = new Set(['validation_status', 'missing_data']);

// ─── Component ──────────────────────────────────────────────────────

const GridSortDialog: React.FC<GridSortDialogProps> = ({
  open, onClose, sortSpecs, onApply, columns, maxLevels = 3,
}) => {
  const [localSpecs, setLocalSpecs] = useState<GridSortSpec[]>([]);
  const [selectedIdx, setSelectedIdx] = useState<number>(0);

  // Sync local state when dialog opens
  React.useEffect(() => {
    if (open) {
      setLocalSpecs(sortSpecs.length > 0 ? [...sortSpecs] : []);
      setSelectedIdx(0);
    }
  }, [open, sortSpecs]);

  // Sortable columns (exclude virtual/derived that can't be server-sorted)
  const sortableColumns = useMemo(
    () => columns.filter(c => !UNSORTABLE_FIELDS.has(c.key)),
    [columns]
  );

  // Available columns (not already used, except the one at selectedIdx)
  const getAvailableColumns = useCallback((currentIdx: number) => {
    const usedFields = new Set(
      localSpecs.map((s, i) => i !== currentIdx ? s.field : null).filter(Boolean)
    );
    return sortableColumns.filter(c => !usedFields.has(c.key));
  }, [localSpecs, sortableColumns]);

  // ─── Handlers ───────────────────────────────────────────────────

  const handleAddLevel = useCallback(() => {
    if (localSpecs.length >= maxLevels) return;
    const usedFields = new Set(localSpecs.map(s => s.field));
    const nextCol = sortableColumns.find(c => !usedFields.has(c.key));
    if (!nextCol) return;
    setLocalSpecs(prev => [...prev, { field: nextCol.key, direction: 'asc' }]);
    setSelectedIdx(localSpecs.length);
  }, [localSpecs, sortableColumns, maxLevels]);

  const handleDeleteLevel = useCallback(() => {
    if (localSpecs.length === 0) return;
    setLocalSpecs(prev => prev.filter((_, i) => i !== selectedIdx));
    setSelectedIdx(prev => Math.max(0, Math.min(prev, localSpecs.length - 2)));
  }, [localSpecs, selectedIdx]);

  const handleCopyLevel = useCallback(() => {
    if (localSpecs.length === 0 || selectedIdx >= localSpecs.length || localSpecs.length >= maxLevels) return;
    const source = localSpecs[selectedIdx];
    const usedFields = new Set(localSpecs.map(s => s.field));
    const nextCol = sortableColumns.find(c => !usedFields.has(c.key));
    if (!nextCol) return;
    const copy: GridSortSpec = { field: nextCol.key, direction: source.direction };
    setLocalSpecs(prev => {
      const next = [...prev];
      next.splice(selectedIdx + 1, 0, copy);
      return next;
    });
    setSelectedIdx(selectedIdx + 1);
  }, [localSpecs, selectedIdx, sortableColumns, maxLevels]);

  const handleMoveUp = useCallback(() => {
    if (selectedIdx <= 0) return;
    setLocalSpecs(prev => {
      const next = [...prev];
      [next[selectedIdx - 1], next[selectedIdx]] = [next[selectedIdx], next[selectedIdx - 1]];
      return next;
    });
    setSelectedIdx(prev => prev - 1);
  }, [selectedIdx]);

  const handleMoveDown = useCallback(() => {
    if (selectedIdx >= localSpecs.length - 1) return;
    setLocalSpecs(prev => {
      const next = [...prev];
      [next[selectedIdx], next[selectedIdx + 1]] = [next[selectedIdx + 1], next[selectedIdx]];
      return next;
    });
    setSelectedIdx(prev => prev + 1);
  }, [selectedIdx, localSpecs.length]);

  const handleFieldChange = useCallback((idx: number, field: string) => {
    setLocalSpecs(prev => prev.map((s, i) => i === idx ? { ...s, field } : s));
  }, []);

  const handleDirectionChange = useCallback((idx: number, direction: 'asc' | 'desc') => {
    setLocalSpecs(prev => prev.map((s, i) => i === idx ? { ...s, direction } : s));
  }, []);

  const handleApply = useCallback(() => {
    onApply(localSpecs);
    onClose();
  }, [localSpecs, onApply, onClose]);

  const handleClearAll = useCallback(() => {
    setLocalSpecs([]);
    setSelectedIdx(0);
  }, []);

  const canAddMore = localSpecs.length < maxLevels && localSpecs.length < sortableColumns.length;

  // Group columns for the select dropdown
  const groupedColumns = useMemo(() => {
    const groups: Record<string, GridColumnDef[]> = {};
    for (const col of sortableColumns) {
      if (!groups[col.group]) groups[col.group] = [];
      groups[col.group].push(col);
    }
    return groups;
  }, [sortableColumns]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[640px] w-full p-0 gap-0">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-400" />
            <DialogTitle className="text-base font-semibold">Data Grid Sort</DialogTitle>
          </div>
          <DialogDescription className="text-xs text-muted-foreground mt-1">
            Define how the main data grid rows are sorted. Up to {maxLevels} sort levels are sent as Supabase <code className="text-[10px] bg-secondary/50 px-1 rounded">.order()</code> calls for server-side sorting.
          </DialogDescription>
        </DialogHeader>

        {/* ─── Toolbar ─── */}
        <div className="px-5 py-2.5 border-b border-border/50 flex items-center gap-2 bg-secondary/20">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAddLevel}
                  disabled={!canAddMore}
                  className="h-7 text-xs border-border text-muted-foreground hover:text-foreground gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Level
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Add a sort level (max {maxLevels})
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDeleteLevel}
                  disabled={localSpecs.length === 0}
                  className="h-7 text-xs border-border text-red-400/70 hover:text-red-400 hover:border-red-700/50 gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete Level
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Remove the selected sort level</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyLevel}
                  disabled={localSpecs.length === 0 || !canAddMore}
                  className="h-7 text-xs border-border text-muted-foreground hover:text-foreground gap-1"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy Level
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Duplicate the selected sort level</TooltipContent>
            </Tooltip>

            <div className="w-px h-5 bg-border mx-1" />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleMoveUp}
                  disabled={selectedIdx <= 0 || localSpecs.length === 0}
                  className="h-7 w-7 p-0 border-border text-muted-foreground hover:text-foreground"
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Move selected level up (higher priority)</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleMoveDown}
                  disabled={selectedIdx >= localSpecs.length - 1 || localSpecs.length === 0}
                  className="h-7 w-7 p-0 border-border text-muted-foreground hover:text-foreground"
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Move selected level down (lower priority)</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="flex-1" />

          {localSpecs.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleClearAll}
              className="h-7 text-xs text-muted-foreground/60 hover:text-red-400"
            >
              Clear All
            </Button>
          )}
        </div>

        {/* ─── Sort Levels Table ─── */}
        <div className="px-5 py-3 min-h-[180px] max-h-[360px] overflow-y-auto">
          {localSpecs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
              <Database className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm">No sort levels defined.</p>
              <p className="text-xs mt-1 opacity-60">
                Click "Add Level" to define how the main grid data is ordered.
              </p>
              <div className="flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-md bg-blue-900/10 border border-blue-700/20 text-[10px] text-blue-400">
                <Info className="w-3 h-3 flex-shrink-0" />
                <span>Without custom sort, the grid defaults to Account ID ascending with secondary sorts on series, account, and date.</span>
              </div>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider w-[80px]"></th>
                  <th className="text-left py-2 px-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Column</th>
                  <th className="text-left py-2 px-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider w-[140px]">Sort On</th>
                  <th className="text-left py-2 px-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider w-[140px]">Order</th>
                </tr>
              </thead>
              <tbody>
                {localSpecs.map((spec, idx) => {
                  const isSelected = idx === selectedIdx;
                  const availableCols = getAvailableColumns(idx);
                  const currentCol = sortableColumns.find(c => c.key === spec.field);

                  return (
                    <tr
                      key={idx}
                      onClick={() => setSelectedIdx(idx)}
                      className={`border-b border-border/30 cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-blue-500/10 border-l-2 border-l-blue-400'
                          : 'hover:bg-secondary/30'
                      }`}
                    >
                      {/* Label */}
                      <td className="py-2 px-2">
                        <span className={`text-[11px] font-semibold ${isSelected ? 'text-blue-400' : 'text-muted-foreground'}`}>
                          {idx === 0 ? 'Sort by' : 'Then by'}
                        </span>
                      </td>

                      {/* Column Picker */}
                      <td className="py-2 px-2">
                        <Select
                          value={spec.field}
                          onValueChange={(v) => handleFieldChange(idx, v)}
                        >
                          <SelectTrigger className="h-8 text-xs bg-background border-border">
                            <SelectValue placeholder="Select column...">
                              {currentCol ? `${currentCol.label}` : 'Select column...'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="max-h-[280px]">
                            {/* Show current column first if not in available list */}
                            {currentCol && !availableCols.find(c => c.key === currentCol.key) && (
                              <SelectItem key={currentCol.key} value={currentCol.key}>
                                <span className="flex items-center gap-2">
                                  <span>{currentCol.label}</span>
                                  <span className="text-[9px] text-muted-foreground/50">{currentCol.group}</span>
                                </span>
                              </SelectItem>
                            )}
                            {Object.entries(groupedColumns).map(([group, cols]) => {
                              const filteredCols = cols.filter(c =>
                                availableCols.some(ac => ac.key === c.key) || c.key === spec.field
                              );
                              if (filteredCols.length === 0) return null;
                              return (
                                <React.Fragment key={group}>
                                  <div className="px-2 py-1 text-[9px] font-semibold text-muted-foreground/60 uppercase tracking-wider bg-secondary/30 sticky top-0">
                                    {group}
                                  </div>
                                  {filteredCols.map(col => (
                                    <SelectItem key={col.key} value={col.key}>
                                      {col.label}
                                    </SelectItem>
                                  ))}
                                </React.Fragment>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </td>

                      {/* Sort On (always Cell Values for server-side) */}
                      <td className="py-2 px-2">
                        <Select value="values" disabled>
                          <SelectTrigger className="h-8 text-xs bg-background border-border opacity-70">
                            <SelectValue>Cell Values</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="values">Cell Values</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>

                      {/* Order */}
                      <td className="py-2 px-2">
                        <Select
                          value={spec.direction}
                          onValueChange={(v) => handleDirectionChange(idx, v as 'asc' | 'desc')}
                        >
                          <SelectTrigger className="h-8 text-xs bg-background border-border">
                            <SelectValue>
                              {spec.direction === 'asc' ? 'A to Z / Low to High' : 'Z to A / High to Low'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asc">A to Z / Low to High</SelectItem>
                            <SelectItem value="desc">Z to A / High to Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ─── Info Bar ─── */}
        {localSpecs.length > 0 && (
          <div className="px-5 py-2 border-t border-border/30 bg-blue-900/5">
            <div className="flex items-start gap-2 text-[10px] text-blue-400/80">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <div>
                <p>
                  These {localSpecs.length} sort level{localSpecs.length !== 1 ? 's' : ''} will be sent as
                  server-side <code className="bg-secondary/50 px-1 rounded">.order()</code> calls to Supabase.
                  {localSpecs.length < maxLevels && (
                    <> Additional tiebreaker sorts (account_series, account_id, pu_date) are appended automatically for fields not already specified.</>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ─── Footer ─── */}
        <DialogFooter className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            className="h-8 text-xs text-muted-foreground hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleApply}
            className="h-8 text-xs bg-blue-600 hover:bg-blue-500 text-white"
          >
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GridSortDialog;
